import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = "/api/zerodha";

const ACTION_STYLES = {
  ADD: { bg: "var(--tint-pos-strong)", border: "#059669", color: "#34d399", icon: "+" },
  CUT: { bg: "#7f1d1d", border: "#dc2626", color: "#f87171", icon: "-" },
  TIGHTEN: { bg: "#78350f", border: "#d97706", color: "#fbbf24", icon: "~" },
  WARNING: { bg: "#1e3a5f", border: "#3b82f6", color: "#93c5fd", icon: "!" },
};

function AlertCard({ alert, onAction }) {
  const style = ACTION_STYLES[alert.action] || ACTION_STYLES.WARNING;
  const timeStr = alert.timestamp
    ? new Date(alert.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
    : "";

  return (
    <div style={{
      background: style.bg, border: `1px solid ${style.border}`, borderRadius: 8,
      padding: "12px 16px", marginBottom: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            background: style.border, color: "#fff", fontWeight: 800, fontSize: 13,
            width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
          }}>{style.icon}</span>
          <span style={{ color: style.color, fontWeight: 700, fontSize: 14 }}>{alert.action}</span>
          <span style={{ color: "var(--grey-text)", fontSize: 12 }}>{alert.type} {alert.index}</span>
          <span style={{ color: "var(--btn-border)", fontSize: 11 }}>{alert.tradingsymbol}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "var(--grey-text2)", fontSize: 11 }}>{timeStr}</span>
          {alert.confidence && (
            <span style={{
              fontSize: 10, padding: "2px 6px", borderRadius: 4, fontWeight: 700,
              background: alert.confidence === "HIGH" ? "#166534" : alert.confidence === "MEDIUM" ? "#854d0e" : "#1e3a5f",
              color: alert.confidence === "HIGH" ? "#4ade80" : alert.confidence === "MEDIUM" ? "#fcd34d" : "#93c5fd",
            }}>{alert.confidence}</span>
          )}
        </div>
      </div>

      <div style={{ color: "var(--grey-light)", fontSize: 12, marginBottom: 6, lineHeight: 1.5 }}>
        {alert.reason}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 11, color: "var(--grey-text2)" }}>
          {alert.entryPremium && <>Prem: <strong>₹{alert.entryPremium?.toFixed(1)}</strong> → <strong style={{ color: (alert.premiumPnlPct || 0) >= 0 ? "#4ade80" : "#f87171" }}>₹{alert.currentPremium?.toFixed(1)}</strong>
          {alert.premiumPnlPct != null && <> (<span className={(alert.premiumPnlPct || 0) >= 0 ? "positive" : "negative"}>{alert.premiumPnlPct >= 0 ? "+" : ""}{alert.premiumPnlPct}%</span>)</>}
          {alert.premiumPnl != null && <> {" | "}<span className={alert.premiumPnl >= 0 ? "positive" : "negative"}>{alert.premiumPnl >= 0 ? "+" : ""}₹{Math.abs(alert.premiumPnl).toLocaleString("en-IN")}</span></>}
          {" | "}</>}
          {alert.minsOpen > 0 && <>{alert.minsOpen}m open{" | "}</>}
          <span className={alert.rMultiple >= 0 ? "positive" : "negative"}>{alert.rMultiple >= 0 ? "+" : ""}{alert.rMultiple}R</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {alert.action === "ADD" && (
            <button className="btn btn-sm" style={{ background: "#059669", color: "#fff", fontSize: 11, padding: "4px 10px", border: "none", borderRadius: 4, cursor: "pointer" }}
              onClick={() => onAction("add", alert)}>Add 1 Lot</button>
          )}
          {alert.action === "CUT" && (
            <button className="btn btn-sm" style={{ background: "#dc2626", color: "#fff", fontSize: 11, padding: "4px 10px", border: "none", borderRadius: 4, cursor: "pointer" }}
              onClick={() => onAction("cut", alert)}>Cut 50%</button>
          )}
          {alert.action === "TIGHTEN" && alert.suggestedSL && (
            <button className="btn btn-sm" style={{ background: "#d97706", color: "#fff", fontSize: 11, padding: "4px 10px", border: "none", borderRadius: 4, cursor: "pointer" }}
              onClick={() => onAction("tighten", alert)}>Move SL to {alert.suggestedSL}</button>
          )}
          <button className="btn btn-sm" style={{ background: "var(--surface3)", color: "var(--grey-text)", fontSize: 10, padding: "4px 8px", border: "1px solid var(--edge)", borderRadius: 4, cursor: "pointer" }}
            onClick={() => onAction("dismiss", alert)}>Dismiss</button>
        </div>
      </div>

      {alert.suggestion && (
        <div style={{ marginTop: 6, padding: "6px 8px", background: "rgba(0,0,0,0.2)", borderRadius: 4, fontSize: 11, color: "var(--grey-text)", fontStyle: "italic" }}>
          {alert.suggestion}
        </div>
      )}
    </div>
  );
}

export default function TradeAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [openTrades, setOpenTrades] = useState(0);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState("10");
  const [dismissed, setDismissed] = useState(new Set());
  const [activeView, setActiveView] = useState("live"); // live | history | backtest

  // Backtest state
  const [btData, setBtData] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [btError, setBtError] = useState("");
  const [btDays, setBtDays] = useState("5");
  const [btIndex, setBtIndex] = useState("NIFTY");
  const [btTimeframe, setBtTimeframe] = useState("15minute");
  const [btTarget, setBtTarget] = useState("2");
  const [btLots, setBtLots] = useState("5");
  const [btDateMode, setBtDateMode] = useState("days");
  const [btFromDate, setBtFromDate] = useState("");
  const [btToDate, setBtToDate] = useState("");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const intervalRef = useRef(null);
  const prevAlertCount = useRef(0);

  const playAlertSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = 660; gain.gain.value = 0.5;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.stop(ctx.currentTime + 0.3);
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2); gain2.connect(ctx.destination);
        osc2.type = "sine"; osc2.frequency.value = 880; gain2.gain.value = 0.5;
        osc2.start();
        gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        osc2.stop(ctx.currentTime + 0.3);
      }, 200);
    } catch {}
  }, [soundEnabled]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get("/api/alerts/active", { timeout: 10000 });
      if (res.data.success) {
        const newAlerts = res.data.alerts.filter(a => !dismissed.has(`${a.tradeId}_${a.action}_${a.timestamp}`));
        if (newAlerts.length > prevAlertCount.current) {
          playAlertSound();
        }
        prevAlertCount.current = newAlerts.length;
        setAlerts(newAlerts);
        setOpenTrades(res.data.openTrades);
        setError("");
      }
    } catch (err) {
      if (err.response?.status !== 401) setError(err.response?.data?.message || err.message);
    }
  }, [dismissed, playAlertSound]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get("/api/alerts/history?limit=50", { timeout: 5000 });
      if (res.data.success) setHistory(res.data.alerts);
    } catch {}
  }, []);

  useEffect(() => {
    fetchAlerts();
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchAlerts, parseInt(refreshInterval) * 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refreshInterval, fetchAlerts]);

  useEffect(() => {
    if (activeView === "history") fetchHistory();
  }, [activeView, fetchHistory]);

  const runAlertBacktest = useCallback(async () => {
    setBtLoading(true); setBtError(""); setBtData(null);
    try {
      const params = {
        timeframe: btTimeframe, index: btIndex, target: btTarget, lots: btLots,
        days: btDateMode === "custom" ? "0" : btDays,
        ...(btDateMode === "custom" && { fromDate: btFromDate, toDate: btToDate }),
      };
      const res = await axios.get("/api/alerts/backtest", { params, timeout: 120000 });
      if (res.data.success) setBtData(res.data.data);
      else setBtError(res.data.message);
    } catch (err) { setBtError(err.response?.data?.message || err.message); }
    setBtLoading(false);
  }, [btTimeframe, btIndex, btTarget, btLots, btDays, btDateMode, btFromDate, btToDate]);

  const handleAction = useCallback(async (action, alert) => {
    if (action === "dismiss") {
      setDismissed(prev => new Set([...prev, `${alert.tradeId}_${alert.action}_${alert.timestamp}`]));
      setAlerts(prev => prev.filter(a => !(a.tradeId === alert.tradeId && a.action === alert.action && a.timestamp === alert.timestamp)));
      return;
    }
    if (action === "add") {
      window.alert(`ADD action noted for ${alert.tradingsymbol}. Manual execution required — place order from the strategy tab.`);
    } else if (action === "cut") {
      window.alert(`CUT action noted for ${alert.tradingsymbol}. Manual execution required — exit partial from the strategy tab.`);
    } else if (action === "tighten" && alert.suggestedSL) {
      try {
        await axios.post(`${API}/auto-trade/update`, { id: alert.tradeId, currentSL: alert.suggestedSL });
        setAlerts(prev => prev.filter(a => !(a.tradeId === alert.tradeId && a.action === "TIGHTEN")));
      } catch (err) {
        window.alert(`Failed to update SL: ${err.message}`);
      }
    }
  }, []);

  const alertCounts = alerts.reduce((acc, a) => { acc[a.action] = (acc[a.action] || 0) + 1; return acc; }, {});

  return (
    <div className="five-ema" style={{ padding: "0 12px" }}>
      <div className="dashboard-header">
        <div>
          <h2>Trade Alerts</h2>
          <p className="dashboard-subtitle">Real-time position advisor for open trades</p>
        </div>
        <div className="dashboard-actions" style={{ gap: 8 }}>
          <select className="ema-select" value={refreshInterval} onChange={e => setRefreshInterval(e.target.value)}>
            <option value="5">5s</option><option value="10">10s</option><option value="30">30s</option><option value="60">1m</option>
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--grey-text)" }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} /> Auto
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--grey-text)" }}>
            <input type="checkbox" checked={soundEnabled} onChange={e => setSoundEnabled(e.target.checked)} /> Sound
          </label>
          <button className="btn btn-sm btn-primary" onClick={fetchAlerts}>Refresh</button>
        </div>
      </div>

      <div style={{
        display: "flex", gap: 16, padding: "8px 0", fontSize: 12, color: "var(--grey-text2)",
        borderBottom: "1px solid var(--surface3)", marginBottom: 8, flexWrap: "wrap", alignItems: "center",
      }}>
        <span>Open Trades: <strong style={{ color: openTrades > 0 ? "#ffa726" : "var(--btn-border)" }}>{openTrades}</strong></span>
        <span>Active Alerts: <strong style={{ color: alerts.length > 0 ? "#4ade80" : "var(--btn-border)" }}>{alerts.length}</strong></span>
        {alertCounts.ADD > 0 && <span style={{ color: "#34d399" }}>+{alertCounts.ADD} ADD</span>}
        {alertCounts.CUT > 0 && <span style={{ color: "#f87171" }}>{alertCounts.CUT} CUT</span>}
        {alertCounts.TIGHTEN > 0 && <span style={{ color: "#fbbf24" }}>{alertCounts.TIGHTEN} TIGHTEN</span>}
        {alertCounts.WARNING > 0 && <span style={{ color: "#93c5fd" }}>{alertCounts.WARNING} WARNING</span>}
        {autoRefresh && <span style={{ marginLeft: "auto", color: "var(--edge)" }}>
          <span className="pulse-dot" style={{ display: "inline-block", marginRight: 4 }}></span>
          Polling every {refreshInterval}s
        </span>}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button className={`btn btn-sm ${activeView === "live" ? "btn-primary" : ""}`} onClick={() => setActiveView("live")}>
          Live Alerts {alerts.length > 0 && `(${alerts.length})`}
        </button>
        <button className={`btn btn-sm ${activeView === "history" ? "btn-primary" : ""}`} onClick={() => setActiveView("history")}>
          History
        </button>
        <button className={`btn btn-sm ${activeView === "backtest" ? "btn-primary" : ""}`} onClick={() => setActiveView("backtest")}>
          Backtest
        </button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* ===== BACKTEST VIEW ===== */}
      {activeView === "backtest" && (
        <div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, alignItems: "center" }}>
            <select className="ema-select" value={btIndex} onChange={e => setBtIndex(e.target.value)}>
              <option value="NIFTY">NIFTY</option><option value="BANKNIFTY">BANKNIFTY</option><option value="SENSEX">SENSEX</option>
            </select>
            <select className="ema-select" value={btTimeframe} onChange={e => setBtTimeframe(e.target.value)}>
              <option value="5minute">5 Min</option><option value="15minute">15 Min</option><option value="30minute">30 Min</option>
            </select>
            <select className="ema-select" value={btDateMode} onChange={e => setBtDateMode(e.target.value)}>
              <option value="days">By Days</option><option value="custom">Custom Dates</option>
            </select>
            {btDateMode === "days" ? (
              <select className="ema-select" value={btDays} onChange={e => setBtDays(e.target.value)}>
                <option value="1">1 Day</option><option value="3">3 Days</option><option value="5">5 Days</option>
                <option value="10">10 Days</option><option value="30">30 Days</option><option value="60">60 Days</option>
              </select>
            ) : (
              <>
                <input type="date" className="ema-select date-input" value={btFromDate} onChange={e => setBtFromDate(e.target.value)} />
                <input type="date" className="ema-select date-input" value={btToDate} onChange={e => setBtToDate(e.target.value)} />
              </>
            )}
            <select className="ema-select" value={btTarget} onChange={e => setBtTarget(e.target.value)}>
              <option value="1">1R</option><option value="1.5">1.5R</option><option value="2">2R</option><option value="3">3R</option>
            </select>
            <select className="ema-select" value={btLots} onChange={e => setBtLots(e.target.value)}>
              <option value="1">1 Lot</option><option value="2">2 Lots</option><option value="5">5 Lots</option>
            </select>
            <button className="btn btn-primary" onClick={runAlertBacktest} disabled={btLoading}>
              {btLoading ? "Running..." : "Run Alert Backtest"}
            </button>
          </div>

          {btError && <div className="error-msg">{btError}</div>}
          {btLoading && <div className="loading"><div className="spinner" /><p>Running alert backtest...</p></div>}

          {btData && !btLoading && (() => {
            const s = btData.summary;
            return (
              <div>
                {/* Impact Banner */}
                <div className={`bt-money-banner ${s.totalImprovement >= 0 ? "money-profit" : "money-loss"}`}>
                  <div className="bt-money-amount">
                    {s.totalImprovement >= 0 ? "+" : ""}₹{Math.abs(s.totalImprovement).toLocaleString("en-IN")}
                  </div>
                  <div className="bt-money-detail">
                    Alert impact: {s.improvementPct >= 0 ? "+" : ""}{s.improvementPct}% on {s.totalTrades} trades | {s.period}
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="bt-summary-grid">
                  <div className="bt-card"><div className="bt-card-value">{s.totalTrades}</div><div className="bt-card-label">Trades Analyzed</div></div>
                  <div className="bt-card"><div className="bt-card-value">{s.tradesWithAlerts}</div><div className="bt-card-label">Trades with Alerts</div></div>
                  <div className="bt-card bt-card-green"><div className="bt-card-value">{s.tradesImproved}</div><div className="bt-card-label">Improved</div></div>
                  <div className="bt-card bt-card-red"><div className="bt-card-value">{s.tradesWorsened}</div><div className="bt-card-label">Worsened</div></div>
                  <div className={`bt-card ${s.totalOriginalPnl >= 0 ? "bt-card-green" : "bt-card-red"}`}>
                    <div className="bt-card-value">₹{s.totalOriginalPnl?.toLocaleString("en-IN")}</div><div className="bt-card-label">Original P&L</div>
                  </div>
                  <div className={`bt-card ${s.totalAdjustedPnl >= 0 ? "bt-card-green" : "bt-card-red"}`}>
                    <div className="bt-card-value">₹{s.totalAdjustedPnl?.toLocaleString("en-IN")}</div><div className="bt-card-label">With Alerts P&L</div>
                  </div>
                  <div className="bt-card"><div className="bt-card-value">{s.totalAlerts}</div><div className="bt-card-label">Total Alerts</div></div>
                </div>

                {/* Alert Type Breakdown */}
                {s.alertCounts && Object.keys(s.alertCounts).length > 0 && (
                  <div style={{ display: "flex", gap: 16, padding: "8px 0", fontSize: 12, marginBottom: 12 }}>
                    {Object.entries(s.alertCounts).map(([action, count]) => {
                      const st = ACTION_STYLES[action] || ACTION_STYLES.WARNING;
                      return <span key={action} style={{ color: st.color }}>{action}: <strong>{count}</strong></span>;
                    })}
                  </div>
                )}

                {/* Per-Trade Results */}
                <div className="stocks-table-wrapper">
                  <table className="stocks-table">
                    <thead><tr>
                      <th>Date</th><th>Type</th><th>Fib</th><th>Entry</th><th>Exit</th><th>Reason</th>
                      <th>Original P&L</th><th>With Alerts</th><th>Impact</th><th>Alerts</th>
                    </tr></thead>
                    <tbody>
                      {btData.tradeResults?.map((t, i) => (
                        <tr key={i} className={t.improvement > 0 ? "trade-win" : t.improvement < 0 ? "trade-loss" : ""}>
                          <td style={{ fontSize: 11 }}>{t.triggerDate ? new Date(t.triggerDate).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                          <td><span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span></td>
                          <td><span className="fib-badge">{t.fibLevel || "-"}</span></td>
                          <td>{t.entry?.toFixed(1)}</td>
                          <td>{t.exitPrice?.toFixed(1)}</td>
                          <td style={{ fontSize: 10 }}>{t.exitReason}</td>
                          <td className={t.originalPnl >= 0 ? "positive" : "negative"}>
                            {t.originalPnl >= 0 ? "+" : ""}₹{Math.abs(t.originalPnl).toLocaleString("en-IN")}
                          </td>
                          <td className={t.adjustedPnl >= 0 ? "positive" : "negative"}>
                            {t.adjustedPnl >= 0 ? "+" : ""}₹{Math.abs(t.adjustedPnl).toLocaleString("en-IN")}
                          </td>
                          <td className={t.improvement > 0 ? "positive" : t.improvement < 0 ? "negative" : ""} style={{ fontWeight: 700 }}>
                            {t.improvement > 0 ? "+" : ""}{t.improvement !== 0 ? `₹${Math.abs(t.improvement).toLocaleString("en-IN")}` : "-"}
                          </td>
                          <td>
                            {t.alerts?.length > 0 ? (
                              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                {t.alerts.map((a, j) => {
                                  const st = ACTION_STYLES[a.action] || ACTION_STYLES.WARNING;
                                  return (
                                    <span key={j} title={`${a.reason}${a.impactLabel ? " | " + a.impactLabel : ""}`}
                                      style={{ color: st.color, fontSize: 10, padding: "1px 4px", background: st.bg, borderRadius: 3, border: `1px solid ${st.border}`, cursor: "help" }}>
                                      {a.action} {a.impactLabel || ""}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : <span style={{ color: "var(--edge)", fontSize: 10 }}>none</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {activeView === "live" && (
        <div>
          {alerts.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "var(--edge)" }}>
              {openTrades === 0
                ? "No open trades to monitor. Alerts will appear when you have active positions."
                : "No alerts right now. All positions look stable."}
            </div>
          ) : (
            alerts.map((alert, i) => (
              <AlertCard key={`${alert.tradeId}_${alert.action}_${i}`} alert={alert} onAction={handleAction} />
            ))
          )}
        </div>
      )}

      {activeView === "history" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "var(--grey-text2)" }}>{history.length} alerts in history</span>
            {history.length > 0 && (
              <button className="btn btn-sm btn-danger" onClick={async () => {
                await axios.post("/api/alerts/clear").catch(() => {});
                setHistory([]);
              }}>Clear History</button>
            )}
          </div>
          {history.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "var(--edge)" }}>No alert history yet.</div>
          ) : (
            <div className="stocks-table-wrapper">
              <table className="stocks-table">
                <thead><tr>
                  <th>Time</th><th>Action</th><th>Confidence</th><th>Index</th><th>Type</th><th>Symbol</th>
                  <th>Entry</th><th>LTP</th><th>R</th><th>Reason</th>
                </tr></thead>
                <tbody>
                  {history.slice().reverse().map((a, i) => {
                    const st = ACTION_STYLES[a.action] || ACTION_STYLES.WARNING;
                    return (
                      <tr key={i}>
                        <td style={{ fontSize: 11 }}>{a.timestamp ? new Date(a.timestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                        <td><span style={{ color: st.color, fontWeight: 700, fontSize: 11 }}>{a.action}</span></td>
                        <td style={{ fontSize: 10 }}>{a.confidence}</td>
                        <td style={{ fontSize: 11 }}>{a.index}</td>
                        <td><span className={`signal-badge ${a.type === "SHORT" ? "badge-short" : "badge-long"}`}>{a.type}</span></td>
                        <td style={{ fontSize: 10 }}>{a.tradingsymbol}</td>
                        <td>{a.entry?.toFixed(1)}</td>
                        <td>{a.currentPrice?.toFixed(1)}</td>
                        <td className={a.rMultiple >= 0 ? "positive" : "negative"}>{a.rMultiple >= 0 ? "+" : ""}{a.rMultiple}R</td>
                        <td style={{ fontSize: 10, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.reason}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
